/**
 * `PlateLabel` — the procedural dubplate label. `docs/design-direction.md` §6.5.
 *
 * Most tracks in the channel have no artwork, so every track gets *cut* a label from
 * its own metadata: two curated riso inks, one of three plate patterns, a catalogue
 * number, a side, and the title scrawled across the blank in chinagraph at a hashed
 * tilt. It is the signature system of the whole app, and its entire value is that the
 * derivation is deterministic forever — the same track cuts the same plate in the
 * indexer, in the API and in the browser, today and after a re-post.
 *
 * **The hash is not re-implemented here.** `derivePlate` / `plateFromSeed` /
 * `plateCssVars` / `inkPairFor` all come from `@bl/shared`, where they are pinned by
 * literal expected values in tests. A local FNV-1a in the view layer would drift from
 * the stored `plate_seed` the first time anyone "improved" the normaliser, and every
 * label in the app would silently re-cut. When the row carries a persisted `seed`,
 * this component prefers it over re-hashing, so a title cleaned up after indexing
 * never changes the record it was pressed on.
 *
 * **Six hand-tested ink pairs, never generated hues** (§6.5): procedural hue maths
 * would hand roughly one track in ten an ugly plate and destroy the trust the concept
 * depends on. Adding or reordering a pair re-cuts every existing plate, which is why
 * the array lives in the shared package as a data contract.
 *
 * The three patterns are all **annular** — ink prints as rings around a clear stock
 * centre, which is where the catalogue line, the scrawl and the artist sit. That is
 * how a 7" label is actually set, and it is also what keeps every glyph at
 * `--text-on-paper` on `--bg-paper` (13.8:1) instead of stranding 11px type on an ink
 * whose contrast §12.9 forbids: cream on `--warm-fill` is 2.8:1 and is banned outright.
 *
 * **Cover art, when the channel supplied it, is composited inside the label only**
 * (§6.5, §12.22) — never a full-bleed hero, never a grid — duotoned to the plate's
 * two inks by `texture.css`'s `.plate__art` and masked into the same annulus as the
 * ink patterns. Bad 90s JPEGs become consistent riso prints; the channel's worst
 * asset becomes the app's most coherent one.
 *
 * The scrawl is Caveat 600 at `--fs-20` — inside §6.5's 18–24px window *and* on
 * §4.3's closed scale, so it grows with Telegram's largest-font setting instead of
 * being pinned to a literal. Caveat appears nowhere else in this file, and nowhere in
 * the app but here and the set-list numbering (§12.15).
 */

import type { CSSProperties, JSX } from 'react';

import { derivePlate, hasCyrillic, plateCssVars, plateFromSeed } from '@bl/shared';
import type { Plate } from '@bl/shared';

import styles from './components.module.css';
import { cx } from './primitives/Rule';

export interface PlateLabelProps {
  readonly title: string;
  /** `null` when genuinely unknown — never `''`, never a placeholder (§12.6). */
  readonly performer?: string | null | undefined;
  /** The Telegram message id, which is where the catalogue number comes from. */
  readonly messageId: number;
  /**
   * `tracks.plate_seed`, when the row carries it. Preferred over re-hashing: a plate
   * must not change because the title was cleaned up after indexing.
   */
  readonly seed?: number | undefined;
  /** `BL-0421` as the API sent it. Falls back to the derived number. */
  readonly catalogueNo?: string | undefined;
  /** `/api/cover/:id?t=…`, or `null` when the channel supplied no artwork. */
  readonly coverUrl?: string | null | undefined;
  readonly className?: string | undefined;
}

export function PlateLabel({
  title,
  performer = null,
  messageId,
  seed,
  catalogueNo,
  coverUrl = null,
  className,
}: PlateLabelProps): JSX.Element {
  const plate: Plate =
    seed === undefined ? derivePlate(performer, title, messageId) : plateFromSeed(seed, messageId);

  const cat = catalogueNo ?? plate.catalogueNo;
  const hasArt = typeof coverUrl === 'string' && coverUrl !== '';
  const style = plateCssVars(plate) as CSSProperties;

  return (
    <div
      className={cx('disc__label', styles.plate, className)}
      style={style}
      data-pattern={String(plate.pattern)}
      data-side={plate.side}
      data-pair={String(plate.pair)}
      data-seed={String(plate.seed)}
    >
      {hasArt ? (
        <span className={cx('plate__art', styles.art)} aria-hidden="true">
          {/* Decorative: the title and the artist are set on the label beside it, so a
              second reading of the same name would be noise. */}
          <img src={coverUrl} alt="" />
        </span>
      ) : (
        <>
          <span className={styles.ink} aria-hidden="true" />
          <span className={cx(styles.ink, styles.ink2)} aria-hidden="true" />
        </>
      )}

      <span className={cx('t-mono', 't-11', styles.cat)}>
        {cat} · {plate.side}
      </span>

      <span className={cx('t-scrawl', 't-20', styles.scrawl)}>{title}</span>

      {performer === null || performer === undefined || performer === '' ? null : (
        <span className={cx('t-mono', 't-11', styles.artist, hasCyrillic(performer) && styles.cyr)}>
          {performer}
        </span>
      )}
    </div>
  );
}
